import { boolean, foreignKey, index, integer, jsonb, pgTable, text, timestamp, uniqueIndex, varchar } from "drizzle-orm/pg-core";
import { organisations, tutorProfiles, users } from "./schemaBase.js";
import { sql } from "drizzle-orm";

export interface PaidPurchase {
  source_key: string;
  kind: "lesson" | "block";
  provider_id: number;
  customer_id: number;
  portal_student_id: string | null;
  student_name: string | null;
  payer_name: string;
  payer_email: string | null;
  service_name: string;
  quantity: number;
  duration_minutes: number | null;
  lesson_at: string | null;
  expires_at: string | null;
  paid_at: string;
  confirmed_at: string;
  amount: string;
  currency: string;
  payment_method: string;
  payment_ids: number[];
  appointment_id: number | null;
  booking_id: number | null;
  block_id: string | null;
  original_price: string | null;
  discount_amount: string | null;
}

export interface PaidInvoiceSnapshot {
  reference: string;
  issuedAt: string;
  issuerName: string;
  issuerAddress: string;
  issuerEmail: string;
  issuerLegalName: string;
  /** Legacy issued snapshots only. New invoices do not collect or print payer addresses. */
  payerAddress?: string;
  tutorName: string;
  studentName: string;
  purchase: PaidPurchase;
  correction?: { revision: number; originalReference: string; previousReference: string; reason: string };
  paymentDateOnly?: boolean;
}

export const paymentInvoiceProfiles = pgTable("payment_invoice_profiles", {
  id: varchar("id").primaryKey(),
  organisationId: varchar("organisation_id").notNull().references(() => organisations.id),
  userId: varchar("user_id").notNull().references(() => users.id),
  tutorId: varchar("tutor_id").notNull().references(() => tutorProfiles.id),
  providerId: integer("provider_id").notNull(),
  enabled: boolean("enabled").notNull().default(false),
  activatedAt: timestamp("activated_at", { withTimezone: true }),
  issuerName: text("issuer_name").notNull(),
  issuerAddress: text("issuer_address").notNull().default(""),
  issuerEmail: text("issuer_email").notNull().default(""),
  issuerLegalName: text("issuer_legal_name").notNull().default(""),
  vatNotChargedConfirmed: boolean("vat_not_charged_confirmed").notNull().default(false),
  numberPrefix: text("number_prefix"),
  nextBlockCheckAt: timestamp("next_block_check_at", { withTimezone: true }).notNull().defaultNow(),
  blockCheckedAt: timestamp("block_checked_at", { withTimezone: true }),
  receiptScanSince: timestamp("receipt_scan_since", { withTimezone: true }),
  receiptScanBefore: timestamp("receipt_scan_before", { withTimezone: true }),
  receiptScanOffset: integer("receipt_scan_offset").notNull().default(0),
  blockScanSince: timestamp("block_scan_since", { withTimezone: true }),
  blockScanBefore: timestamp("block_scan_before", { withTimezone: true }),
  blockScanOffset: integer("block_scan_offset").notNull().default(0),
  nextReconcileAt: timestamp("next_reconcile_at", { withTimezone: true }).notNull().defaultNow(),
  appointmentSweepComplete: boolean("appointment_sweep_complete").notNull().default(false),
  invoiceSweepComplete: boolean("invoice_sweep_complete").notNull().default(false),
  nextNumber: integer("next_number").notNull().default(1),
  scanAfter: text("scan_after"),
  refreshAfter: text("refresh_after"),
  driveToken: text("drive_token"),
  driveFolderId: text("drive_folder_id"),
  driveAccountEmail: text("drive_account_email"),
  driveError: text("drive_error"),
  driveConnectionVersion: integer("drive_connection_version").notNull().default(0),
  lastCheckedAt: timestamp("last_checked_at", { withTimezone: true }),
  lastError: text("last_error"),
}, (t) => [uniqueIndex("payment_invoice_profiles_account_idx").on(t.organisationId, t.userId),
  uniqueIndex("payment_invoice_profiles_prefix_idx").on(t.organisationId, t.numberPrefix)]);

/** Frozen server-verified choices; never supplied as raw purchase IDs by the browser. */
export interface InvoiceHistorySelection { sourceKey: string; fingerprint: string }
export interface InvoiceHistoryPreviewRow {
  sourceKey: string;
  studentName: string | null;
  payerName: string;
  serviceName: string;
  kind: "lesson" | "block";
  quantity: number;
  lessonAt: string | null;
  paidAt: string;
  amount: string;
  paymentMethod: string;
  reference: string | null;
  note: "already_invoiced" | "learner_unresolved" | "invoice_owner_conflict" | null;
  selectionToken: string | null;
}
export interface InvoiceHistoryPreviewPage {
  rows: InvoiceHistoryPreviewRow[];
  nextCursor: string | null;
  expiresAt: string;
  issueCount: number;
}

export const paymentInvoiceHistoryRequests = pgTable("payment_invoice_history_requests", {
  id: varchar("id").primaryKey(),
  profileId: varchar("profile_id").notNull(),
  paidFrom: timestamp("paid_from", { withTimezone: true }),
  paidBefore: timestamp("paid_before", { withTimezone: true }).notNull(),
  scanBefore: timestamp("scan_before", { withTimezone: true }).notNull(),
  phase: text("phase").notNull().default("receipts"),
  selectedPurchases: jsonb("selected_purchases").$type<InvoiceHistorySelection[]>(),
  pageOffset: integer("page_offset").notNull().default(0),
  scanAfter: text("scan_after"),
  checkedCount: integer("checked_count").notNull().default(0),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  completedAt: timestamp("completed_at", { withTimezone: true }),
}, (t) => [
  foreignKey({ name: "payment_invoice_history_profile_fk", columns: [t.profileId], foreignColumns: [paymentInvoiceProfiles.id] }),
  uniqueIndex("payment_invoice_history_active_idx").on(t.profileId).where(sql`${t.phase} <> 'completed'`),
]);

export const paidPaymentInvoices = pgTable("paid_payment_invoices", {
  id: varchar("id").primaryKey(),
  organisationId: varchar("organisation_id").notNull().references(() => organisations.id),
  profileId: varchar("profile_id").notNull().references(() => paymentInvoiceProfiles.id),
  historyRequestId: varchar("history_request_id"),
  sourceKey: text("source_key").notNull(),
  reference: text("reference"),
  status: text("status").notNull().default("needs_attention"),
  snapshot: jsonb("snapshot").$type<PaidInvoiceSnapshot>(),
  fingerprint: text("fingerprint"),
  pdf: text("pdf"),
  driveFileId: text("drive_file_id"),
  driveError: text("drive_error"),
  driveStatus: text("drive_status").notNull().default("pending"),
  driveAttempts: integer("drive_attempts").notNull().default(0),
  driveNextAttemptAt: timestamp("drive_next_attempt_at", { withTimezone: true }).notNull().defaultNow(),
  error: text("error"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
  foreignKey({ name: "paid_payment_invoices_history_fk", columns: [t.historyRequestId], foreignColumns: [paymentInvoiceHistoryRequests.id] }),
  uniqueIndex("paid_payment_invoices_source_idx").on(t.organisationId, t.sourceKey),
  uniqueIndex("paid_payment_invoices_reference_idx").on(t.organisationId, t.reference),
  index("paid_payment_invoices_profile_idx").on(t.profileId, t.createdAt),
]);

export const paymentInvoiceOAuthStates = pgTable("payment_invoice_oauth_states", {
  stateHash: text("state_hash").primaryKey(),
  profileId: varchar("profile_id").notNull(),
  userId: varchar("user_id").notNull().references(() => users.id),
  expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
}, (t) => [foreignKey({ name: "payment_invoice_oauth_states_profile_fk", columns: [t.profileId], foreignColumns: [paymentInvoiceProfiles.id] })]);

/** Append-only replacement documents; original purchases, references and PDFs stay untouched. */
export const paymentInvoiceCorrections = pgTable("payment_invoice_corrections", {
  id: varchar("id").primaryKey(),
  invoiceId: varchar("invoice_id").notNull(),
  profileId: varchar("profile_id").notNull(),
  revision: integer("revision").notNull(),
  requestKey: varchar("request_key").notNull(),
  requestHash: text("request_hash").notNull(),
  createdBy: varchar("created_by").notNull(),
  reason: text("reason").notNull(),
  snapshot: jsonb("snapshot").$type<PaidInvoiceSnapshot>().notNull(),
  pdf: text("pdf").notNull(),
  driveFileId: text("drive_file_id"),
  driveStatus: text("drive_status").notNull().default("pending"),
  driveError: text("drive_error"),
  driveAttempts: integer("drive_attempts").notNull().default(0),
  driveNextAttemptAt: timestamp("drive_next_attempt_at", { withTimezone: true }).notNull().defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => [
  uniqueIndex("payment_invoice_corrections_revision_idx").on(t.invoiceId, t.revision),
  uniqueIndex("payment_invoice_corrections_request_idx").on(t.invoiceId, t.requestKey),
  index("payment_invoice_corrections_profile_idx").on(t.profileId, t.createdAt),
  foreignKey({ name: "payment_invoice_corrections_invoice_fk", columns: [t.invoiceId], foreignColumns: [paidPaymentInvoices.id] }),
  foreignKey({ name: "payment_invoice_corrections_profile_fk", columns: [t.profileId], foreignColumns: [paymentInvoiceProfiles.id] }),
  foreignKey({ name: "payment_invoice_corrections_creator_fk", columns: [t.createdBy], foreignColumns: [users.id] }),
]);

/** Retained for existing records; paid invoices no longer read or write payer addresses. */
export const paymentInvoiceBillingDetails = pgTable("payment_invoice_billing_details", {
  id: varchar("id").primaryKey(),
  profileId: varchar("profile_id").notNull(),
  customerId: integer("customer_id").notNull(),
  address: text("address").notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
}, (t) => [uniqueIndex("payment_invoice_billing_details_customer_idx").on(t.profileId, t.customerId),
  foreignKey({ name: "payment_invoice_billing_details_profile_fk", columns: [t.profileId], foreignColumns: [paymentInvoiceProfiles.id] })]);
